import { Dialog as DialogPrimitive } from 'radix-ui'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Modal de conteúdo: um assunto que precisa da tela inteira por um instante (um código QR, uma
 * escolha curta), sem a pergunta destrutiva do `ConfirmDialog`. Mesma moldura: fio, pampas, a
 * única sombra permitida (`shadow-float`), título em Lexend, Esc fecha e o foco volta a quem abriu.
 * Abaixo de 900px ocupa a largura menos 48px; acima, o máximo que `size` diz.
 */
export interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** Uma linha dizendo o que o modal faz: é a descrição acessível. */
  description?: ReactNode
  children?: ReactNode
  /** As ações no rodapé, alinhadas à direita ("Fechar", "Copiar link"). */
  actions?: ReactNode
  /** Largura máxima acima de 900px: `sm` 470px (padrão) ou `md` 640px. */
  size?: 'sm' | 'md'
  className?: string
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  actions,
  size = 'sm',
  className,
}: DialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="bg-overlay data-[state=open]:animate-in data-[state=open]:fade-in fixed inset-0 z-40" />
        <DialogPrimitive.Content
          data-slot="dialog"
          // Sem descrição, o Radix avisaria no console; dizemos de propósito que não há.
          {...(description ? {} : { 'aria-describedby': undefined })}
          className={cn(
            'border-hairline-heavy bg-pampas shadow-float fixed top-1/2 left-1/2 z-50 max-h-[calc(100dvh-48px)] w-[calc(100%-48px)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto border px-7 py-[26px] outline-none',
            size === 'md' ? 'max-w-[640px]' : 'max-w-[470px]',
            className,
          )}
        >
          <DialogPrimitive.Title className="font-display text-ink text-[20px] leading-[1.25] font-light text-pretty">
            {title}
          </DialogPrimitive.Title>
          {description ? (
            <DialogPrimitive.Description className="font-body text-body text-secondary mt-3 font-light">
              {description}
            </DialogPrimitive.Description>
          ) : null}
          {children ? <div className="mt-5">{children}</div> : null}
          {actions ? (
            <div className="mt-5 flex flex-wrap items-center justify-end gap-[10px]">{actions}</div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

/** O botão que fecha o modal sem precisar do estado de quem abriu: envolve qualquer `Button`. */
export const DialogClose = DialogPrimitive.Close
